import React, { useMemo } from 'react';
import { ReactComponent as JoistEdgeMainLogo }
  from '../assets/images/joist_edge_main_logo.svg';
import { ReactComponent as EdgeOnlyHDOnboardingHomeDepotLogo }
  from '../assets/images/homedepot_logo.svg';
import { ReactComponent as EdgeMorePartnersComingLogo }
  from '../assets/images/more_partners_coming.svg';
import { ReactComponent as EverProMainLogo }
  from '../assets/images/ever_pro_main_logo.svg';
import {
  EVERPRO_DOMAIN,
  JOIST_DOMAIN,
  SERVICE_NATION_BY_EVER_PRO_DOMAIN,
  UNKNOWN_DOMAIN,
  getAppDomain,
} from './domains';
import { ReactComponent as JoistEllipseDesktop }
  from '../assets/images/joist_ellipse_desktop.svg';
import { ReactComponent as JoistEllipseMobile }
  from '../assets/images/joist_ellipse_mobile.svg';
import { ReactComponent as EverProEllipseDesktop }
  from '../assets/images/everPro_ellipse_desktop.svg';
import { ReactComponent as EverProEllipseMobile }
  from '../assets/images/everPro_ellipse_mobile.svg';
import { ReactComponent as WalletRewardsEverPro }
  from '../assets/images/wallet_rewards_everpro.svg';
import { ReactComponent as WalletRewardsSNByEverPro }
  from '../assets/images/wallet_rewards_snByEverpro.svg';
import { ReactComponent as WalletRewardsJoist }
  from '../assets/images/wallet_rewards_joist.svg';
import { ReactComponent as EmptyWalletEverPro }
  from '../assets/images/empty_wallet_everpro.svg';
import { ReactComponent as EmptyWalletSNByEverPro }
  from '../assets/images/empty_wallet_snByEverpro.svg';
import { ReactComponent as EmptyWalletJoist }
  from '../assets/images/empty_wallet_joist.svg';
import { Logger } from './logging/logger';

const logger = Logger.create('useLogo');

// manage app Logos based on the current domain
export function useLogo() {
  const logos = useMemo<{
    [key: string]: {
      [key: string]: React.FunctionComponent<React.SVGProps<SVGSVGElement> & {
        title?: string | undefined;
      }> | string | null
    }
  }>(() => ({
    [JOIST_DOMAIN]: {
      main: JoistEdgeMainLogo,
      secondaryOne: 'https://d2o7bp9oz3gksv.cloudfront.net/joist_edge_green_logo_002ecc659c.svg',
      onlyHDOnboardingHomeDepot: EdgeOnlyHDOnboardingHomeDepotLogo,
      morePartnersComing: EdgeMorePartnersComingLogo,
      ellipseMobile: JoistEllipseMobile,
      ellipseDesktop: JoistEllipseDesktop,
      emptyWallet: EmptyWalletJoist,
      walletRewards: WalletRewardsJoist,
      loginImage: 'https://d2o7bp9oz3gksv.cloudfront.net/login_page_logo_joist_802297e8c0.svg',
    },
    [EVERPRO_DOMAIN]: {
      main: EverProMainLogo,
      secondaryOne: 'https://d2o7bp9oz3gksv.cloudfront.net/ever_pro_yellow_logo_0328475fa0.svg',
      onlyHDOnboardingHomeDepot: EdgeOnlyHDOnboardingHomeDepotLogo,
      morePartnersComing: EdgeMorePartnersComingLogo,
      ellipseMobile: EverProEllipseMobile,
      ellipseDesktop: EverProEllipseDesktop,
      emptyWallet: EmptyWalletEverPro,
      walletRewards: WalletRewardsEverPro,
      loginImage: 'https://d2o7bp9oz3gksv.cloudfront.net/login_page_logo_ever_Pro_ea53cd4abc.svg',
    },
    [SERVICE_NATION_BY_EVER_PRO_DOMAIN]: {
      main: 'https://d2o7bp9oz3gksv.cloudfront.net/sn_by_ever_Pro_horizontal_color_29fc7c217e.svg',
      secondaryOne: 'https://d2o7bp9oz3gksv.cloudfront.net/sn_by_ever_Pro_horizontal_color_29fc7c217e.svg',
      onlyHDOnboardingHomeDepot: EdgeOnlyHDOnboardingHomeDepotLogo,
      morePartnersComing: EdgeMorePartnersComingLogo,
      ellipseMobile: EverProEllipseMobile,
      ellipseDesktop: EverProEllipseDesktop,
      emptyWallet: EmptyWalletSNByEverPro,
      walletRewards: WalletRewardsSNByEverPro,
      loginImage: 'https://d2o7bp9oz3gksv.cloudfront.net/login_page_logo_sn_by_ever_Pro_29fb73bc64.png',
    },
    [UNKNOWN_DOMAIN]: {
      main: null,
    }, // for backward compatibility with web.dev.optimusapp.io and web.staging.optimusapp.io
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const sharedLogos: {
    [key: string]: string;
  } = {
    capitalWrapsLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/capital_wraps_d673a606dd.png',
    spartanToolLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/spartan_tool_3662356b69.png',
    winsupplyLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/winsupply_b9ec86faa5.png',
    weSupplyTradesLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/wesupplytrades_cb83f934ce.png',
    homeDepotLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/home_depot_7c8024f6aa.png',
    coastLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/coast_3ab48e84c3.png',
    hdSupplyLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/hdsupply_2d5bd6da17.png',
    sunbeltRentalsLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/sunbelt_rentals_vertical_0db48c0736.png',
    everConnectLogo: 'https://d2o7bp9oz3gksv.cloudfront.net/Ever_Connect_240x240_92f9e46078.png',
  };

  const appDomain = useMemo(() => {
    const computedDomain = getAppDomain();
    logger.debug(Logger.format(`App domain: ${computedDomain}`));
    return computedDomain;
  }, []);

  const domainLogo = logos[appDomain];
  return { ...sharedLogos, ...domainLogo };
}
